import * as React from 'react';
import * as c from 'models/constants';
import { translate } from 'react-i18next';
import { reduxForm, getFormSyncErrors, Field, FieldArray } from 'redux-form';
import { IShopOrderShipping, IOrder, IShopOrderShippingPackage, IShopOrderShippingDocument } from 'models/orders/order';
import { connect } from 'react-redux';
import { IStore } from 'redux/IStore';
import { required, maxLength, minValue, isNumber } from 'helpers/validationattributes';
import { IDomainValuesAction } from 'models/domainvalues/domainvalues';
import { getCountryDomainValues, getCountryCallingCodeDomainValues } from 'modules/domainvalues';
import { ButtonsSection, PortalPageSection } from 'components/PortalStructure';
import { IDomainValue, ICountryCallingCodeDomainValue } from 'models/common';
import { getShop } from 'modules/shop/manageshop';
import { IManageShopAction } from 'models/shop/manageshop';
import { ErrorSummary, Button, WhiteButton } from 'components';
import { ShopAddressForm } from 'containers/Common/AddressForms/ShopAddressForm';
import { OrderAddressForm } from 'containers/Common/AddressForms/OrderAddressForm';
import { FileUpload, PortalInputField, PortalSelectMenu } from 'components/FormControls';
import { FieldArrayFieldsProps, FieldArrayMetaProps } from 'models/common/redux-form';
import { createOrderShipping } from 'redux/modules/shop/shopordercard';

require('./style.css');

interface IOwnProps {
	shippingId: string;
	shopOrderId: string;
	order: IOrder;
	shopId: string;
}

interface IProps extends IOwnProps {
	shopOrderShipping: IShopOrderShipping;

	currentFormValues: IShopOrderShipping;
	initialValues: IShopOrderShipping;

	countryDomainValues: IDomainValue[];
	countryCallingCodeDomainValues: ICountryCallingCodeDomainValue[];
	shippingError?: string;

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;
	change?: (path: string, value: any) => void;

	/* functions props */
	t?: (key: string) => string;
	getCountryDomainValues?: () => IDomainValuesAction;
	getCountryCallingCodeDomainValues?: () => IDomainValuesAction;
	getShop: (id: string, isShipping: boolean) => IManageShopAction;
	createOrderShipping: (form: IShopOrderShipping) => IManageShopAction;
	array?: {
		insert: (field: string, index: number, value: any) => void;
		push: (field: string, value: any) => void;
	};
}

const maxLength50 = maxLength(50);
const minValue1 = minValue(1);

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopOrderShippingForm extends React.Component<IProps, any> {
	constructor(props) {
		super(props);

		this.renderPackages = this.renderPackages.bind(this);
		this.renderPackage = this.renderPackage.bind(this);
		this.renderDocuments = this.renderDocuments.bind(this);
		this.renderDocument = this.renderDocument.bind(this);
		this.uploadDocuments = this.uploadDocuments.bind(this);
		this.onSubmit = this.onSubmit.bind(this);
		this.validateArray = this.validateArray.bind(this);
	}

	public componentDidMount() {
		this.props.getShop(this.props.shopId, false);
		this.props.getCountryDomainValues();
		this.props.getCountryCallingCodeDomainValues();
	}

	public onSubmit(form: IShopOrderShipping) {
		const { t } = this.props;
		let phoneNumber = form.order.shippingPhoneNumber;
		if (form.order.shippingCallingCodeKey) {
			const prefix = t(`DV_CountryCallingCodes_${form.order.shippingCallingCodeKey}`);
			if (prefix.length < 20) {
				phoneNumber = prefix + phoneNumber;
			}
			form = {
				...form,
				order: {
					...form.order,
					shippingPhoneNumber: phoneNumber,
				},
			};
		}
		this.props.createOrderShipping(form);
	}

	private validateArray(value: any[]) {
		return value && Array.isArray(value) && value.length ? undefined : this.props.t('Validation_Client_Field_Required');
	}

	private uploadDocuments(files: File[]): Promise<void> {
		const promises = files.map(
			(file) =>
				new Promise((resolve) => {
					const name = file.name;
					const reader = new FileReader();
					reader.onload = () => {
						let binary = '';
						const bytes = new Uint8Array(reader.result as ArrayBuffer);
						const len = bytes.byteLength;
						for (let i = 0; i < len; i++) {
							binary += String.fromCharCode(bytes[i]);
						}
						const content = window.btoa(binary);
						const document: IShopOrderShippingDocument = { name, content };
						this.props.array.push('shipping.trade_documents', document);
						resolve(undefined);
					};

					// Read in the image file as a data URL.
					reader.readAsArrayBuffer(file);
				}),
		);
		return Promise.all(promises).then(() => undefined);
	}

	private uploadFailed() {
		alert('Upload failed');
	}

	private renderDocument(name: string, i: number, all: FieldArrayFieldsProps<IShopOrderShippingDocument>): JSX.Element {
		const { submitting, t } = this.props;
		return (
			<div className="row">
				<div className="col-md-8">
					<Field
						name={name + '.name'}
						component={PortalInputField}
						label={t('Orders_OrderShipping_Label_Document') + ' ' + (i + 1)}
						readOnly={true}
					/>
				</div>
				<div className="col-md-4 buttonHolderCol">
					<WhiteButton type="button" disabled={submitting} onClick={() => all.remove(i)} usage="admin">
						- {t('Orders_OrderShipping_Button_RemoveDocument')}
					</WhiteButton>
				</div>
			</div>
		);
	}

	private renderDocuments({
		fields,
		meta,
	}: {
		fields: FieldArrayFieldsProps<IShopOrderShippingDocument>;
		meta: FieldArrayMetaProps;
	}): JSX.Element {
		const { t } = this.props;
		return (
			<>
				{fields.map(this.renderDocument)}
				<div>
					<FileUpload
						t={t}
						name={'trade_document'}
						fileUploadFailed={this.uploadFailed}
						acceptFileTypes=""
						isMultiple={true}
						isButton={true}
						buttonText={'+ ' + t('Orders_OrderShipping_Button_AddDocument')}
						showLoader={() => this.setState({ submitting: true })}
						hideLoader={() => this.setState({ submitting: false })}
						customFileHandler={this.uploadDocuments}
						usage="admin"
					/>
				</div>
				{meta.submitFailed &&
					((meta.error && <span className="errorInputPosition requiredfield">{meta.error}</span>) ||
						(meta.warning && <span>{meta.warning}</span>))}
			</>
		);
	}

	private renderPackage(name: string, i: number, all: FieldArrayFieldsProps<IShopOrderShippingPackage>): JSX.Element {
		const { submitting, t } = this.props;
		return (
			<React.Fragment key={i}>
				<h3>{t('Orders_OrderShipping_Package_Title') + ' ' + (i + 1)}</h3>
				<hr />

				<div className="row">
					<div className="col-md-4">
						<Field
							name={name + '.height'}
							component={PortalInputField}
							type="text"
							inputMode="decimal"
							label={t('Orders_OrderShipping_Label_PackageHeight')}
							validate={[required, minValue1, isNumber]}
						/>
					</div>
					<div className="col-md-4">
						<Field
							name={name + '.width'}
							component={PortalInputField}
							type="text"
							inputMode="decimal"
							label={t('Orders_OrderShipping_Label_PackageWidth')}
							validate={[required, minValue1, isNumber]}
						/>
					</div>
					<div className="col-md-4">
						<Field
							name={name + '.length'}
							component={PortalInputField}
							type="text"
							inputMode="decimal"
							label={t('Orders_OrderShipping_Label_PackageLength')}
							validate={[required, minValue1, isNumber]}
						/>
					</div>
				</div>

				<div className="row">
					<div className="col-md-4">
						<Field
							name={name + '.volume'}
							component={PortalInputField}
							type="text"
							inputMode="numeric"
							label={t('Orders_OrderShipping_Label_PackageVolume')}
							validate={[required, minValue1, isNumber]}
						/>
					</div>
					<div className="col-md-4">
						<Field
							name={name + '.weight'}
							component={PortalInputField}
							type="text"
							inputMode="numeric"
							label={t('Orders_OrderShipping_Label_PackageWeight')}
							validate={[required, minValue1, isNumber]}
						/>
					</div>
					{all.length > 1 ? (
						<div className="col-md-4 buttonHolderCol">
							<WhiteButton
								type="button"
								disabled={submitting || all.length <= 1}
								onClick={() => all.remove(i)}
								usage="admin"
							>
								- {t('Orders_OrderShipping_Button_RemovePackage')}
							</WhiteButton>
						</div>
					) : undefined}
				</div>
			</React.Fragment>
		);
	}

	private renderPackages({
		fields,
		meta,
	}: {
		fields: FieldArrayFieldsProps<IShopOrderShippingPackage>;
		meta: FieldArrayMetaProps;
	}): JSX.Element {
		const { submitting, t } = this.props;
		return (
			<>
				{fields.map(this.renderPackage)}
				<div>
					<WhiteButton type="button" disabled={submitting} onClick={() => fields.push({} as any)} usage="admin">
						+ {t('Orders_OrderShipping_Button_AddPackage')}
					</WhiteButton>
				</div>
				{meta.submitFailed &&
					((meta.error && <span className="errorInputPosition requiredfield">{meta.error}</span>) ||
						(meta.warning && <span>{meta.warning}</span>))}
			</>
		);
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		const packagesPrefix = 'shipping.packages';
		if (key.slice(0, packagesPrefix.length) === packagesPrefix) {
			const parts = key.split('.');
			switch (parts[parts.length - 1]) {
				case 'height':
					return t('Orders_OrderShipping_Label_PackageHeight');
				case 'width':
					return t('Orders_OrderShipping_Label_PackageWidth');
				case 'length':
					return t('Orders_OrderShipping_Label_PackageLength');
				case 'volume':
					return t('Orders_OrderShipping_Label_PackageVolume');
				case 'weight':
					return t('Orders_OrderShipping_Label_PackageWeight');
				default:
					break;
			}
		}
		switch (key) {
			case 'sender.shippingName':
				return t('Shop_ManageShop_ShippingAddress_Label_Name');
			case 'sender.shippingAddress':
				return t('Shop_ManageShop_ShippingAddress_Label_Address');
			case 'sender.shippingCity':
				return t('Shop_ManageShop_ShippingAddress_Label_City');
			case 'sender.shippingPostalCode':
				return t('Shop_ManageShop_ShippingAddress_Label_PostalCode');
			case 'sender.shippingFromCountryKey':
				return t('Shop_ManageShop_ShippingAddress_Label_CountryCode');
			case 'sender.shippingProvince':
				return t('Shop_ManageShop_ShippingAddress_Label_Province');
			case 'sender.shippingEmail':
				return t('Shop_ManageShop_ShippingAddress_Label_Email');
			case 'sender.shippingPhone':
				return t('Shop_ManageShop_ShippingAddress_Label_Phone');
			case 'sender.shippingNotes':
				return t('Shop_ManageShop_ShippingAddress_Label_Notes');

			case 'order.shippingName':
				return t('Cart_CheckoutAddress_Label_NameAndSurname');
			case 'order.shippingAddressLine':
				return t('Cart_CheckoutAddress_Label_StreetAndNumber');
			case 'order.shippingZIPCode':
				return t('Cart_CheckoutAddress_Label_PostCode');
			case 'order.shippingCity':
				return t('Cart_CheckoutAddress_Label_City');
			case 'order.shippingCountryKey':
				return t('Cart_CheckoutAddress_Label_Country');
			case 'order.email':
				return t('Cart_CheckoutAddress_Label_Email');
			case 'order.shippingPhoneNumber':
				return t('Cart_CheckoutAddress_Label_PhoneNumber');
			case 'order.shippingNote':
				return t('Cart_CheckoutAddress_Placeholder_Note');

			case 'shipping.goods_origin':
				return t('Orders_OrderShipping_Label_GoodsOrigin');
			case 'shipping.goods_type':
				return t('Orders_OrderShipping_Label_GoodsType');

			default:
				return key;
		}
	}

	public render() {
		const { handleSubmit, t, submitFailed, submitting, countryDomainValues, countryCallingCodeDomainValues } =
			this.props;

		const errorSummaryProps = {
			// formErrors,
			submitFailed,
			error: this.props.shippingError ? true : false,
			message: this.props.shippingError ? this.props.shippingError : null,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		const countries = [];
		if (countryDomainValues) {
			countryDomainValues.forEach((country: IDomainValue) => {
				countries.push({
					key: country.key,
					value: t(`DV_Countries_${country.key}`),
				});
			});

			countries.sort(function (a: IDomainValue, b: IDomainValue) {
				if (a.value < b.value) {
					return -1;
				}
				if (a.value > b.value) {
					return 1;
				}
				return 0;
			});
		}

		return (
			<form onSubmit={handleSubmit(this.onSubmit)} className="shopOrderShipping">
				<PortalPageSection usage="admin">
					<div className="row">
						<div className="col-md-6">
							<Field
								name="shipping.goods_origin"
								component={PortalSelectMenu}
								items={countries}
								isRequired={true}
								validate={[required]}
								label={t('Orders_OrderShipping_Label_GoodsOrigin')}
							/>
						</div>
						<div className="col-md-6">
							<Field
								name="shipping.goods_type"
								component={PortalInputField}
								type="text"
								label={t('Orders_OrderShipping_Label_GoodsType')}
								validate={[maxLength50, required]}
							/>
						</div>
					</div>
				</PortalPageSection>
				<PortalPageSection title={t('Orders_OrderShipping_Packages_Title')} usage="admin">
					<FieldArray name="shipping.packages" component={this.renderPackages} validate={this.validateArray} />
				</PortalPageSection>
				<PortalPageSection
					title={t('Orders_OrderShipping_Documents_Title')}
					description={t('Orders_OrderShipping_Documents_Description')}
					usage="admin"
				>
					<FieldArray name="shipping.trade_documents" component={this.renderDocuments} validate={this.validateArray} />
				</PortalPageSection>
				<PortalPageSection title={t('Orders_OrderShipping_Sender_Title')} usage="admin">
					<ShopAddressForm keyPrefix="sender." t={t} countryDomainValues={countryDomainValues} excludeNotes={true} />
				</PortalPageSection>
				<PortalPageSection title={t('Orders_OrderShipping_Address_Title')} usage="admin">
					<OrderAddressForm
						keyPrefix="order."
						t={t}
						countryDomainValues={countryDomainValues}
						countryCallingCodeDomainValues={countryCallingCodeDomainValues}
						shortPhoneNumberField={false}
					/>
				</PortalPageSection>

				<div className="errorBlock">
					<ErrorSummary {...errorSummaryProps} />
				</div>
				<ButtonsSection>
					<div className="row">
						<div className="col-sm-6" />
						<div className="col-sm-6">
							<div className="floatRight">
								<Button type="submit" disabled={submitting} usage="admin">
									{t('Orders_OrderItemCard_Button_Ship')}
								</Button>
								<div className="clear" />
							</div>
							<div className="clear" />
						</div>
					</div>
				</ButtonsSection>
			</form>
		);
	}
}

let ShopOrderShipping = reduxForm({
	form: c.FORM_NAME_ORDER_SHIPPING,
	enableReinitialize: true,
})(ShopOrderShippingForm);

ShopOrderShipping = connect(
	(state: IStore, props: IOwnProps) => {
		let currentFormValues: IShopOrderShipping = null;
		if (state.form[c.FORM_NAME_ORDER_SHIPPING] && state.form[c.FORM_NAME_ORDER_SHIPPING].values) {
			currentFormValues = state.form[c.FORM_NAME_ORDER_SHIPPING].values;
		}

		const initialValues: IShopOrderShipping = {
			sender: state.manageShop.shop,
			order: props.order,
			shipping: {
				goods_origin: state.manageShop.shop ? state.manageShop.shop.shippingFromCountryKey : undefined,
				packages: [{} as any],
				trade_documents: [],
			},
		};

		return {
			initialValues,
			currentFormValues,
			formErrors: getFormSyncErrors(c.FORM_NAME_ORDER_SHIPPING)(state),
			countryDomainValues: state.domainValues.countries,
			countryCallingCodeDomainValues: state.domainValues.countryCallingCodes,
			shippingError: state.shopOrderCard.shippingError,
			submitFailed: Boolean(state.shopOrderCard.shippingError),
		};
	},
	(dispatch, props: IOwnProps) => ({
		getCountryDomainValues: () => dispatch(getCountryDomainValues()),
		getShop: (id: string, isShipping: boolean) => dispatch(getShop(id, isShipping)),
		getCountryCallingCodeDomainValues: () => dispatch(getCountryCallingCodeDomainValues()),
		createOrderShipping: (form: IShopOrderShipping) =>
			dispatch(createOrderShipping(props.shopOrderId, props.shippingId, form as any)),
	}),
)(ShopOrderShipping);

export { ShopOrderShipping };
